import { useInvoices } from '@/data/hubspot/queries/receivables/invoices';
import { InvoiceRequestBody, InvoicesData } from '@/data/hubspot/types/invoices';
import { formatCurrency } from '@/lib/currency-utils';
import { FilePdfTwoTone } from '@ant-design/icons';
import { Table } from 'antd';
import { SortOrder, SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { compact, find, isEqual, lowerCase, startCase, uniqBy } from 'lodash';
import { useEffect, useState } from 'react';

export default function InvoiceList({ closed }: { closed?: boolean }) {
  // Local States
  const [page, setPage] = useState(1);
  const [isDescending, setIsDescending] = useState(true);
  const [sortCol, setSortCol] = useState<string>();
  const [sortOrder, setSortOrder] = useState<SortOrder>();

  useEffect(() => {
    // Reset page when switching between open and closed invoices
    setSortCol(closed ? 'AMOUNT' : 'BALANCE_DUE_AMOUNT');
    setSortOrder('descend');
  }, [closed]);

  // Queries
  const { data, isLoading } = useInvoices({
    page,
    filter_type: closed ? 'CLOSED' : 'OPEN',
    sort_col: (sortCol ?? 'AMOUNT') as unknown as InvoiceRequestBody['sort_col'],
    sort_by: sortOrder === 'descend' ? 'DESC' : 'ASC',
  });

  // Derivatives
  const uniqCustomFields = uniqBy(
    compact(data?.list.flatMap((x) => x.custom_fields)),
    (x) => x!.id
  );

  return (
    <div>
      <Table
        loading={isLoading}
        className="m-4"
        scroll={{ x: 'max-content' }}
        onChange={(_pagination, _filters, sorter) => {
          // Type Narrowing
          const sort = sorter as unknown as SorterResult<InvoicesData>;

          // Check if the change is a sort operation
          const oldSort = { sortCol, sortOrder };
          const newSort = {
            sortCol: sort.columnKey,
            sortOrder: sort.order,
          };
          const isSorted = !isEqual(oldSort, newSort);

          sort.columnKey && setSortCol('' + sort.columnKey);

          // setIsDescending(sort.order);
          setSortOrder(sort.order);
          if (isSorted) {
            setPage(1);
          }
        }}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: false,
          current: page,
          total: data?.total_records,
          onChange(page) {
            setPage(page);
          },
        }}
        columns={[
          {
            title: 'Reference#',
            key: 'reference',
            render: (_, record) => (
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-[var(--brand-primary)]">{record.invoice_title}</span>
                </div>
                {record.invoice_url && (
                  <a
                    className="text-red-500 hover:text-red-600 "
                    href={record.invoice_url}
                    target="_blank"
                  >
                    <FilePdfTwoTone twoToneColor={'#fc0000'} className="text-red-500" />
                  </a>
                )}
              </div>
            ),
          },
          {
            title: 'Close Date',
            key: 'closedDate',
            hidden: !closed,
            render: (_, record) => <div>{dayjs(record.closed_date).format('ll')}</div>,
          },
          {
            title: 'Issued Date',
            key: 'ISSUE_DATE',
            sorter: true,
            sortOrder: sortCol === 'ISSUE_DATE' ? sortOrder : null,
            sortDirections: ['descend', 'ascend', 'descend'],
            render: (_, record) => <div>{dayjs(record.invoice_date).format('ll')}</div>,
          },
          {
            title: 'Amount Due',
            key: 'BALANCE_DUE_AMOUNT',
            sorter: !closed && true,
            sortOrder: sortCol === 'BALANCE_DUE_AMOUNT' ? sortOrder : null,
            sortDirections: ['descend', 'ascend', 'descend'],
            hidden: closed,
            render: (_, record) => (
              <div className="flex flex-col">
                <div>
                  {formatCurrency(record.balance_due_amount, {
                    currency: record.currency,
                  })}
                </div>
                <div>
                  Total{' '}
                  {formatCurrency(record.amount, {
                    currency: record.currency,
                  })}
                </div>
              </div>
            ),
          },
          {
            title: 'Invoice Amount',
            key: 'AMOUNT',
            sorter: true,
            sortOrder: sortCol === 'AMOUNT' ? sortOrder : null,

            sortDirections: ['descend', 'ascend', 'descend'],
            hidden: !closed,
            render: (_, record) => (
              <div className="flex flex-col">
                <div>
                  {formatCurrency(record.amount, {
                    currency: record.currency,
                  })}
                </div>
              </div>
            ),
          },
          {
            title: 'Due Date',
            key: 'DUE_DATE',
            sorter: true,
            sortOrder: sortCol === 'DUE_DATE' ? sortOrder : null,
            hidden: closed,
            render: (_text, record) => (
              <div className="flex flex-col">
                {dayjs(record.due_date).format('ll')}
                <div className="text-red-600">{dayjs(record.due_date).fromNow()}</div>
              </div>
            ),
          },
          {
            title: 'Invoice Status',
            key: 'invoiceStatus',
            hidden: closed,
            render: (_, record) => (
              <div className="flex flex-col">
                {' '}
                <span className="text-red-400">{startCase(lowerCase(record.status))}</span>
                <span>{dayjs(record.status_updated_date).format('ll')}</span>
              </div>
            ),
          },
          {
            title: 'Last Activity',
            hidden: closed,
            key: 'lastActivity',
            render: (_text, record) => (
              <div>
                <div className="text-[var(--brand-primary)]">
                  {startCase(lowerCase(record.last_activity_type))}
                </div>
                {record.last_activity_type && (
                  <div>{dayjs(record.last_activity_date).format('ll')}</div>
                )}
              </div>
            ),
          },
          ...uniqCustomFields.map((field) => ({
            title: field.display_name,
            key: field.id,
            render: (_: unknown, record: InvoicesData) => {
              const customField = find(record.custom_fields, { id: field.id });
              if (!customField) return null;
              if (['DATE', 'DATETIME'].includes(customField.type)) {
                return <div>{dayjs(customField?.value).format('ll')}</div>;
              } else {
                return <div>{customField?.value}</div>;
              }
            },
          })),
        ]}
        dataSource={data?.list}
      />
    </div>
  );
}
